/**
 * Referral link capture — the storefront half of the two-sided referral loop.
 *
 * Runs site-wide (rendered from theme.liquid). Two jobs:
 *   1. Capture ?ref=CODE from any landing URL into a first-party cookie
 *      (birch_ref, 30 days) so it survives the visitor browsing around before
 *      they sign up.
 *   2. On the first LOGGED-IN pageview while that cookie is set, report the
 *      referral to the worker. Birch runs Shopify's HOSTED new customer accounts
 *      (signup happens on Shopify's pages, which the theme can't hook with a form
 *      listener), so we attribute on the authenticated pageview that follows
 *      signup instead. Shopify has already email-verified the account, so the
 *      worker rewards immediately.
 *
 * The tracker path is the relative Shopify App Proxy path (/apps/ai/referral/
 * track) — Shopify HMAC-signs it AND appends logged_in_customer_id, which the
 * worker trusts as the friend's identity. We send only the ref code; the worker
 * reads the friend's email/signup-date/consent from the Admin API (never the
 * client). Plain-IIFE style like assets/birch-children.js so it loads from a
 * bare <script defer>.
 *
 * window.__birchReferral (loggedIn flag) is set by the inline script in
 * snippets/referral-capture.liquid, which runs before this deferred script.
 */

'use strict';

(function () {
  var COOKIE = 'birch_ref';
  var DONE = 'birch_ref_done';
  var MAX_AGE = 60 * 60 * 24 * 30; // 30 days, seconds
  // Reward codes are alnum; cap length so a hostile ?ref can't bloat the cookie.
  var CODE_RX = /^[A-Za-z0-9]{1,32}$/;
  var TRACK_URL = '/apps/ai/referral/track';

  function readCookie(name) {
    var m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : null;
  }

  function setCookie(name, value) {
    document.cookie =
      name + '=' + encodeURIComponent(value) +
      '; max-age=' + MAX_AGE + '; path=/; SameSite=Lax' +
      (location.protocol === 'https:' ? '; Secure' : '');
  }

  function clearCookie(name) {
    document.cookie = name + '=; max-age=0; path=/; SameSite=Lax';
  }

  // ── 1. Capture ?ref into the cookie ────────────────────────────────────────
  function captureFromUrl() {
    var ref;
    try { ref = new URLSearchParams(location.search).get('ref'); }
    catch (_e) { ref = null; }
    if (ref && CODE_RX.test(ref)) setCookie(COOKIE, ref);
  }

  // ── 2. Report on the first logged-in pageview after signup ──────────────────
  function reportIfSignedUp() {
    var ref = readCookie(COOKIE);
    if (!ref) return;

    var ctx = window.__birchReferral;
    if (!ctx || !ctx.loggedIn) return;   // wait until the friend is signed in
    if (readCookie(DONE) === ref) return; // already reported for this code

    fetch(TRACK_URL, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ref: ref }),
    })
      .then(function (r) {
        // A definitive response (any non-5xx) means we're done for this code —
        // whether rewarded or rejected by a guardrail. Stop retrying. A 5xx or
        // network error leaves birch_ref in place so the next pageview retries.
        if (r.status >= 500) return;
        setCookie(DONE, ref);
        clearCookie(COOKIE);
      })
      .catch(function () { /* transient — retry next pageview */ });
  }

  captureFromUrl();
  reportIfSignedUp();
})();
